import React from 'react';
import CollectionPage from '../shared/CollectionPage';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Navigate } from 'react-router-dom';
import MusicPriceValue from './MusicPriceValue';

/**
 * Music Wishlist Dashboard component
 * Uses the generic CollectionPage with music-specific configuration for wishlist
 */
const MusicWishlistDashboard = () => {
  const { isFeatureEnabled } = useFeatureFlags();

  // Check if the music feature is enabled
  if (!isFeatureEnabled('enableMusicFeature')) {
    return <Navigate to="/" replace />;
  }

  // Music-specific filter options (by format)
  const musicFilterOptions = [
    { value: 'Vinyl', label: 'Vinyl' },
    { value: 'CD', label: 'CD' },
    { value: 'Cassette', label: 'Cassette' },
    { value: '8-Track', label: '8-Track' },
    { value: 'Digital', label: 'Digital' },
    { value: 'Box Set', label: 'Box Set' },
    { value: 'Other', label: 'Other' }
  ];

  // Music-specific render props
  const musicRenderProps = {
    getImageUrl: (item) => item.imageUrl,
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => `Artist: ${item.artist || 'Unknown'} • Format: ${item.format || 'Not specified'}`,
    getItemDetails: (item) => `Year: ${item.year || 'N/A'} • Genre: ${item.genre || 'N/A'} • Label: ${item.label || 'N/A'}`,
    showMarketValue: () => true,
    renderPriceValue: (item) => <MusicPriceValue item={item} />,
    isWishlist: true
  };

  // Music-specific filter function (by format)
  const getMusicFilterValue = (item) => item.format;

  // Music-specific edit fields
  const musicEditFields = [
    {
      name: 'name',
      label: 'Album Title',
      type: 'text',
      required: true,
      gridProps: { xs: 12 }
    },
    {
      name: 'artist',
      label: 'Artist',
      type: 'text',
      required: true,
      gridProps: { xs: 12 }
    },
    {
      name: 'format',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'Vinyl', label: 'Vinyl' },
        { value: 'CD', label: 'CD' },
        { value: 'Cassette', label: 'Cassette' },
        { value: '8-Track', label: '8-Track' },
        { value: 'Digital', label: 'Digital' },
        { value: 'Box Set', label: 'Box Set' },
        { value: 'Other', label: 'Other' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'year',
      label: 'Year',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'genre',
      label: 'Genre',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'imageUrl',
      label: 'Image URL',
      type: 'text',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'rating',
      label: 'Rating',
      type: 'rating',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      required: false,
      gridProps: { xs: 12 }
    }
  ];

  // Define table columns for sorting functionality
  const tableColumns = {
    name: { label: 'Album', sortable: true },
    artist: { label: 'Artist', sortable: true },
    format: { label: 'Format', sortable: true },
    year: { label: 'Year', sortable: true },
    genre: { label: 'Genre', sortable: true },
    label: { label: 'Label', sortable: true },
    priority: { label: 'Priority', sortable: true },
    price: { label: 'Price', sortable: true, numeric: true }
  };

  return (
    <CollectionPage
      collectionType="music-wishlist"
      title="Music Wishlist"
      isWishlist={true}
      renderProps={musicRenderProps}
      filterOptions={musicFilterOptions}
      getFilterValue={getMusicFilterValue}
      editFields={musicEditFields}
      tableColumns={tableColumns}
    />
  );
};

export default MusicWishlistDashboard;
